// Automatic app updates for the PWA.
//
// The service worker calls skipWaiting(), so a new version activates as soon as it has
// installed. Once it takes control of the page, the page is running old JS against new
// cached assets, so we reload it. Two guards keep that from being disruptive:
//  - the very first takeover (first ever visit) is not an update, so it never reloads;
//  - if the user is typing in a field, the reload waits until they leave it or the page
//    is hidden.

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

const isUserEditing = (doc) => {
  if (doc.visibilityState === 'hidden') return false;
  const el = doc.activeElement;
  return Boolean(el && el !== doc.body && el.matches?.(EDITABLE));
};

export function enableAutoUpdate(
  container,
  { doc = document, reload = () => window.location.reload() } = {}
) {
  let controlled = Boolean(container.controller);
  let reloading = false;
  let waiting = false;

  const tryReload = () => {
    if (reloading) return;
    if (isUserEditing(doc)) {
      waiting = true;
      return;
    }
    reloading = true;
    reload();
  };

  container.addEventListener('controllerchange', () => {
    if (!controlled) {
      // First install taking control of an uncontrolled page: nothing stale to replace.
      controlled = true;
      return;
    }
    tryReload();
  });

  // Reload a deferred update once the user has left the field or hidden the page.
  const retry = () => {
    if (waiting) {
      waiting = false;
      tryReload();
    }
  };
  doc.addEventListener('focusout', () => setTimeout(retry, 0));
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') retry();
  });
}

// Ask the browser to look for a new service worker whenever the app comes back to the
// foreground (and when connectivity returns), at most once per `minIntervalMs`. Without
// this a long-lived installed app only checks on navigation.
export function scheduleUpdateChecks(
  registration,
  { doc = document, win = window, now = () => Date.now(), minIntervalMs = 60 * 1000 } = {}
) {
  let last = now();
  const check = () => {
    if (now() - last < minIntervalMs) return;
    last = now();
    registration.update().catch(() => {
      // offline or transient failure: try again next time
    });
  };
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'visible') check();
  });
  win.addEventListener('online', check);
}
